import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { useMutation, useQuery } from "@tanstack/react-query"
import { cccdAdmin, type StudentCandidate } from "@/lib/api/cccdAdminClient"
import { EMAIL_RE, createStudentAccount, kindLabel, memberInputFromCandidate } from "@/lib/cccdStudentSearch"
import { toastError, toastSuccess } from "@/lib/toast"

// cccd-team-member-student-link: reemplaza el botón "+ Agregar miembro" por
// una búsqueda en vivo (como invitar un colaborador en GitHub) — se escribe
// nombre o correo, aparecen coincidencias, y "Agregar" en cualquier resultado
// crea la relación equipo-miembro de inmediato (con ficha completa si el
// candidato ya la tiene, o solo con lo conocido si no) — nunca lleva a una
// página aparte a llenar datos primero. Si nadie coincide, se crea la
// cuenta y se va siempre a /instructor/students/:id/perfil — la misma ruta
// que crear un estudiante desde el panel de Estudiantes.
export function AddMemberSearchBar({
  teamId,
  onAdded,
  disabled,
  disabledReason,
}: {
  teamId: string
  onAdded: () => void
  // cccd-team-seedbed-roster-sync: deshabilita la búsqueda cuando el equipo
  // ya alcanzó el cupo máximo de su categoría.
  disabled?: boolean
  disabledReason?: string
}) {
  const navigate = useNavigate()
  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [creatingNew, setCreatingNew] = useState(false)
  const [newName, setNewName] = useState("")
  const [newEmail, setNewEmail] = useState("")

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(query.trim()), 300)
    return () => clearTimeout(timeout)
  }, [query])

  const { data, isFetching: searching } = useQuery({
    queryKey: ["cccd-student-search", debouncedQuery],
    queryFn: () => cccdAdmin.searchStudents(debouncedQuery),
    enabled: debouncedQuery.length >= 2 && !creatingNew,
  })
  const candidates = data?.candidates ?? []

  const addMutation = useMutation({
    mutationFn: (candidate: StudentCandidate) => cccdAdmin.addMember(teamId, memberInputFromCandidate(candidate)),
    onSuccess: (_result, candidate) => {
      toastSuccess(`${candidate.name} se agregó al equipo.`)
      setQuery("")
      setDropdownOpen(false)
      onAdded()
    },
    onError: (err) => toastError(err || "No se pudo agregar el miembro."),
  })

  const createMutation = useMutation({
    mutationFn: () => createStudentAccount(newName.trim(), newEmail.trim()),
    onSuccess: (user) => navigate(`/instructor/students/${user.id}/perfil`),
    onError: (err) => toastError(err || "No se pudo crear la cuenta."),
  })

  function startCreatingNew() {
    const trimmed = query.trim()
    setNewEmail(EMAIL_RE.test(trimmed) ? trimmed : "")
    setNewName(EMAIL_RE.test(trimmed) ? "" : trimmed)
    setCreatingNew(true)
  }

  function cancelCreatingNew() {
    setCreatingNew(false)
    setNewName("")
    setNewEmail("")
  }

  if (disabled) {
    return (
      <div className="w-full max-w-sm rounded-lg border border-dashed border-gray-300 p-2 text-center text-xs text-gray-500 dark:text-gray-400 dark:border-gray-700">
        {disabledReason ?? "Este equipo ya alcanzó su cupo máximo de miembros."}
      </div>
    )
  }

  return (
    <div className="relative w-full max-w-sm">
      <input
        type="text"
        placeholder="Buscar por nombre o correo para agregar un miembro..."
        className="w-full rounded-lg border border-gray-300 p-2 text-sm dark:border-gray-700 dark:bg-gray-900"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setDropdownOpen(true)
        }}
        onFocus={() => setDropdownOpen(true)}
        onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
      />
      {dropdownOpen && query.trim().length >= 2 && (
        <div className="absolute right-0 z-10 mt-1 max-h-96 w-96 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
          {creatingNew ? (
            <div className="space-y-2 p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Se crea la cuenta y se abre su perfil para completar el resto de sus datos.
              </p>
              <input
                type="text"
                placeholder="Nombre completo"
                className="w-full rounded-lg border border-gray-300 p-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
              <input
                type="email"
                placeholder="Correo"
                className="w-full rounded-lg border border-gray-300 p-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={cancelCreatingNew}
                  className="rounded-lg px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  disabled={createMutation.isPending || newName.trim().length < 2 || !EMAIL_RE.test(newEmail.trim())}
                  onClick={() => createMutation.mutate()}
                  className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-60"
                >
                  {createMutation.isPending ? "Creando..." : "Crear estudiante"}
                </button>
              </div>
            </div>
          ) : (
            <>
              {searching ? (
                <p className="p-3 text-xs text-gray-500">Buscando...</p>
              ) : candidates.length > 0 ? (
                candidates.map((candidate) => (
                  <div
                    key={`${candidate.kind}-${candidate.email}`}
                    className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm text-gray-900 dark:text-white">{candidate.name}</p>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {candidate.email} · {kindLabel(candidate.kind)}
                        {candidate.previousMember ? " · ficha completa" : " · ficha por completar"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      disabled={addMutation.isPending}
                      onClick={() => addMutation.mutate(candidate)}
                      className="shrink-0 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-60"
                    >
                      Agregar
                    </button>
                  </div>
                ))
              ) : (
                <p className="p-3 text-xs text-gray-500">Sin coincidencias.</p>
              )}
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={startCreatingNew}
                className="w-full border-t border-gray-100 px-3 py-2 text-left text-sm text-brand-600 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
              >
                + Crear &quot;{query.trim()}&quot; como estudiante nuevo
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
